/**
 * The Automated Smart Nutrient Management System.
 *
 * Pitched above the build, but not uniformly: what is withheld is decided per
 * parameter, by how much publishing it would hand a competitor.
 *
 * Never published: no vendor, board, silicon or capacity; no model feature
 * set, target or performance envelope; no enumeration of the safety checks;
 * and nothing about how the safe operating limits are arrived at.
 *
 * Published deliberately: the layer decomposition and what travels between
 * layers, the mixing unit's actuator complement, the division of the field
 * controller, and a handful of protocol details that any competent engineer
 * would reach for unaided.
 *
 * Rule of thumb when editing: architecture and topology may be described;
 * method, model and limits may not.
 */

export const systemMeta = {
  name: 'Automated Smart Nutrient Management System',
  tagline:
    'End-to-end control from root-zone sensing to verified actuation: closed-loop measurement, deterministic decisioning, and reasoning that runs on site rather than in a cloud.',
  rule: 'Learned models advise. Only deterministic logic actuates.',
  credit: 'SIET · Keystride',
};

/* ---------- System at a glance (6 layers) ----------
 * The decomposition is published: it is what makes the design legible, and a
 * competitor would arrive at a broadly similar split unaided. What each layer
 * is BUILT from stays out.
 */
export const layerGlance = [
  { n: 1, name: 'Field Sensing', sub: 'Hardware', detail: 'Wireless root-zone nodes: moisture, temperature, pH, conductivity, N·P·K' },
  { n: 2, name: 'Transport', sub: 'Hardware + software', detail: 'Long-range low-power radio into a private, on-site network' },
  { n: 3, name: 'Control Panel', sub: 'Edge compute', detail: 'One on-site server carrying every service the control loop needs' },
  { n: 4, name: 'Decision', sub: 'Software', detail: 'Condition Monitor → deterministic decision model → Recipe Engine' },
  { n: 5, name: 'Safety & Actuation', sub: 'Software + hardware', detail: 'Multi-gate validation → confirmed command → mixing unit' },
  { n: 6, name: 'User Interaction', sub: 'Software', detail: 'On-site assistant with grounded retrieval, advisory only' },
];

/* ---------- Guiding invariants ---------- */
/* The design rule itself is stated once, in its own section at the top of the
   System page. It is deliberately not repeated here as an invariant. */
export const invariants = [
  { n: 1, title: 'Offline-first', body: 'All safety-critical operation runs locally on the edge server. Connectivity is an additive overlay that can fail without stopping the farm.' },
  { n: 2, title: 'Books inform, never decide', body: 'The limits the system works within are settled and approved by people in advance. Nothing is consulted, retrieved or looked up while a decision is being made.' },
  { n: 3, title: 'Closed loops everywhere', body: 'Flow verified against command; commands confirmed and non-repeatable; every action answered by a status uplink into the audit trail.' },
  { n: 4, title: 'Fail safe by default', body: 'Valves force-close on link loss, watchdog timeout, or fault. The resting state of the system is not dosing.' },
  { n: 5, title: 'Resource priority', body: 'Shared compute always serves field and decision work ahead of user chat requests.' },
];

/* ---------- End-to-end data flow ----------
 * One path, in the order the architecture draws it: a reading leaves the soil
 * and nothing but a validated command comes back. `edge` is what physically
 * travels to the next step: the label on the arrow, not decoration.
 *
 * Stage 06 is the one exception to naming things plainly here: the number of
 * safety checks and what each inspects stay out, because that set is the
 * design's own judgement about what is sufficient.
 */
export const dataFlow = [
  {
    n: 1, id: 'field', stage: 'Field', body: 'Root-zone node',
    meta: 'battery powered · scheduled wake',
    edge: 'long-range radio uplink',
  },
  {
    n: 2, id: 'transport', stage: 'Transport', body: 'Gateway → network server',
    meta: 'private, on site, no vendor cloud',
    edge: 'message bus · QoS 1',
  },
  {
    n: 3, id: 'ingest', stage: 'Ingest', body: 'Validation service',
    meta: 'range-check · de-duplicate · timestamp',
    edge: 'one validated row',
  },
  {
    n: 4, id: 'store', stage: 'Store', body: 'Operational record',
    meta: 'the single source of validated truth',
    edge: 'latest reading vs crop profile',
  },
  {
    n: 5, id: 'decision', stage: 'Decision', body: 'Condition Monitor → Decision Engine → Recipe Engine',
    meta: 'deficiency · severity · approved ratio',
    edge: 'a recommendation, not a command',
    accent: true,
  },
  {
    n: 6, id: 'safety', stage: 'Safety', body: 'Multi-gate validation',
    meta: 'tested independently of whatever proposed it',
    edge: 'confirmed command',
    accent: true,
  },
  {
    n: 7, id: 'actuation', stage: 'Actuation', body: 'Mixing controller',
    meta: 'pumps · valves · inline flow meter',
    accent: true,
  },
];

export const flowNotes = {
  chainLabel: 'Uplink: soil to valve, in order',
  returnLabel: 'Return path · 07 → 04',
  returnTitle: 'Status uplink into the audit trail',
  returnBody:
    'Actual dispensed volume, acknowledgement and error codes travel back from the mixing controller into the operational record, so the server knows what physically happened, not just what it commanded.',
  branchLabel: 'Branch off step 04 · read-only',
  branchTitle: 'Explanation (non-actuating)',
  branchBody:
    'The on-site assistant reads the operational record and the knowledge base to answer why a recipe was chosen. The branch stops here: no path to the safety engine, no path to an actuator.',
};

/* ---------- Control panel modules ---------- */
export const controlPanelModules = [
  { title: 'Ingestion & Validation', body: 'Decodes every incoming payload, range-checks it, de-duplicates and timestamps it before anything is stored.' },
  { title: 'Operational Record', body: 'Holds field, sensor, measurement, system and historical data: the single source of validated truth.' },
  { title: 'Condition Monitor', body: 'Compares latest readings against the crop profile and flags Normal, Warning or Critical, triggering the scheduler.' },
  { title: 'Decision Engine', body: 'A deterministic model predicts deficiency and severity; the Recipe Engine maps it to an approved fertilizer-to-water ratio.' },
  { title: 'Assistant & Retrieval', body: 'Explanation-only path: structured queries over the record, plus grounded retrieval over the knowledge base.' },
  { title: 'Safety Engine', body: 'Holds every recommendation against a fixed set of independent checks, and authorises nothing that fails any one of them.' },
];

/* ---------- Layer 1 · Field ---------- */
export const fieldLayer = {
  intro: 'Battery-powered, schedule-driven wireless nodes built for slow soil dynamics and long unattended service.',
  controller: {
    title: 'The sensing node',
    points: [
      'Processing and radio integrated in one module, so fewer parts to fail in a field',
      'Enough headroom to sample, poll the probe and run the radio stack unaided',
      'Draws almost nothing between readings, which is what buys the battery its service life',
      'An onboard clock wakes the node only at scheduled times, twice a day',
    ],
  },
  probe: {
    title: 'The soil probe',
    measures: ['Nitrogen', 'Phosphorus', 'Potassium', 'Moisture', 'Temperature', 'pH', 'Conductivity'],
    body: 'Seven values from a single insertion point, on a wired bus chosen for noise tolerance over long field runs, powered from a switched rail and energised only for the seconds a reading takes.',
  },
  cycle: ['Scheduled wake', 'Power probe', 'Read', 'Validate + frame', 'Transmit', 'Radio → sleep'],
};

/* ---------- Layer 2 · Transport ---------- */
export const transportLayer = {
  intro: 'From the radio domain into the application domain, every hop self-hosted and offline-capable.',
  hops: [
    { name: 'Gateway', body: 'Receives many field transmissions concurrently and forwards them onward' },
    { name: 'Network server', body: 'Private and on site; joins, adaptive rates, de-duplication, downlink scheduling' },
    { name: 'Message bus', body: 'Lightweight publish/subscribe; QoS 1 delivery on the command path' },
    { name: 'Validation service', body: 'Parses and range-checks each payload, then writes the row: a deterministic hot path' },
  ],
  travel: [
    { n: 1, title: 'Transmit: field node', body: 'Reading → framed packet → modulated onto the carrier → antenna emits' },
    { n: 2, title: 'Receive: gateway', body: 'Antenna captures the signal → demodulated → integrity checked → forwarded to the network server' },
  ],
  carries: [
    { field: 'Identity', body: 'Which node sent it' },
    { field: 'Sequence', body: 'Ordering and replay protection' },
    { field: 'Payload', body: 'The encrypted reading' },
    { field: 'Integrity', body: 'Proof it arrived unaltered' },
  ],
  note: 'Payloads are encrypted at the node and verified on arrival. Anything failing its integrity check is discarded rather than stored. A corrupt reading must never become a dose.',
};

/* ---------- Layer 3 · Edge compute ---------- */
export const edgeLayer = {
  intro:
    'One permanently powered server on the farm runs ingestion, the record, the decision path, the safety engine and the assistant concurrently, with no dependency on a link to the outside world.',
  why: [
    'Every service stays resident at once, so nothing has to be started before the loop can run',
    'Dedicated acceleration carries the language work, leaving the processor free for control',
    'Local storage keeps the full history and the knowledge base on site',
    'Constant mains power, so the control loop is always available',
  ],
  workloads: [
    'Ingestion & validation',
    'Operational record',
    'Knowledge retrieval',
    'On-site assistant',
    'Messaging & radio services',
    'Safety & decision engines',
  ],
  headroom:
    'The server is specified well above what the control loop needs. Headroom is a safety property here: the loop must never be the thing that gets starved because someone asked the assistant a question.',
  priority: 'Field and decision work always take precedence. Safety reasoning never queues behind a casual query.',
};

/* ---------- Layer 4 · Decision ---------- */
export const decisionLayer = {
  intro: 'Deterministic by design: a fixed model decides, and the Recipe Engine makes it physical.',
  conditionMonitor:
    'Compares the latest readings to the crop profile, flags Normal, Warning or Critical, and triggers the scheduler that invokes the decision engine.',
  /* The model's feature set, its target and its performance envelope together
     amount to a training specification, enough to narrow the approach to a
     handful of candidates. What is published is the contract it honours: the
     same conditions give the same answer, and the answer can be explained.
     Do not restore the input list or the output formulation. */
  decisionModel: {
    title: 'The decision model, deterministic by choice',
    points: [
      'Deterministic: identical conditions always produce an identical answer',
      'Runs entirely on site, with no dependency on a network or an outside service',
      'Every answer can be explained afterwards, and tied to the reading that caused it',
      'Chosen for predictability under real conditions rather than peak accuracy on paper',
    ],
    io: 'IN: everything measured about the zone, and what is growing there → OUT: a requirement the Recipe Engine can act on',
  },
  recipeEngine: {
    title: 'Recipe Engine: requirement to physical dose',
    points: [
      'Takes a requirement, the crop and its stage, the zone, and the stock actually loaded',
      'Returns a strength, a quantity and a duration the hardware can execute',
      'Holds the delivered nutrient to the figure that was asked for, by construction',
      'Between valid answers it rounds DOWN: under-apply, which is recoverable, never over',
    ],
    close: 'Every output is a quantity a pump can actually execute, and a flow meter can afterwards confirm.',
  },

  /* The deck states the dosing relation outright, and that relation is the one
     piece of real method on this page. What is published is therefore the
     SHAPE of the problem (what the solver is handed, and what it has to hand
     back) and never the step that connects the two. Anything added here should
     be checked against that line before it ships. */
  solve: {
    stages: ['measured state', 'required change', 'executable quantity'],
    note: 'Each stage is derived from the zone in front of it. Nothing is read off a schedule, and nothing is carried over from the last time this crop was grown.',
  },
  given: [
    'The shortfall the model found, and how far from target it puts the zone',
    'The crop, the stage it has reached, and the area being fed',
    'The properties of the stock solution actually loaded, not a nominal one',
    'How the line is behaving right now, measured rather than assumed',
  ],
  returns: [
    'One quantity the hardware present is able to dispense',
    'The interval over which to dispense it',
    'A figure the flow meter can be held against once it has',
  ],
  solveClose:
    'Two zones carrying the same crop and different readings get different answers. The same zone with the same readings gets the same answer every time, and that repeatability is what makes the result auditable rather than merely plausible.',
  verify: [
    { k: 'Bounded', v: 'Inside reviewed safe limits for that crop at that growth stage' },
    { k: 'Fulfillable', v: 'Stock solution and water confirmed present before the command is issued' },
    { k: 'Measurable', v: 'Expressed as a volume the inline flow meter is able to verify' },
    { k: 'Reversible', v: 'Rounded down, so the next cycle can correct it' },
    { k: 'Attributable', v: 'Tied to the reading and the crop profile that produced it' },
    { k: 'Verified', v: 'Integrated flow compared against the commanded volume once dispensing ends' },
  ],
  verifyNote:
    'Every computed dose is checked against limits that were agreed and approved before it was ever requested. The dose is only a request until Safety authorises it.',
};

/* ---------- Layer 5 · Safety ----------
 * Enumerating the individual checks would publish the validation design, and
 * with it the judgement that this particular set is sufficient, which is the
 * expensive half. What is described instead is how the layer behaves: where it
 * sits, what it is independent of, and what it does when it refuses. Add
 * properties here freely; never add the contents of a check.
 */
export const safetyProperties = [
  {
    title: 'It cannot be gone around',
    body: 'There is no route to an actuator that avoids this layer. Every proposal, whatever produced it, arrives here first and stops here until it is cleared.',
  },
  {
    title: 'It is independent of what it judges',
    body: 'Nothing that produced a proposal has any influence over whether it passes. The checks are evaluated on their own terms, against conditions fixed before the request existed.',
  },
  {
    title: 'It runs before the command exists',
    body: 'Authorisation happens while the dose is still a request. Nothing is issued to hardware and then reconsidered, so there is no window in which a bad instruction is live.',
  },
  {
    title: 'One failure is enough',
    body: 'The checks are not weighed against one another and no confidence score can outvote them. A single failure ends it, regardless of how strong the rest of the case looks.',
  },
  {
    title: 'Refusing is a designed outcome',
    body: 'A blocked dose is logged, alerted and put in front of a person. The abort path is built and tested as carefully as the path that succeeds. It is not an error case.',
  },
];

export const safetyOutcome = {
  pass: { title: 'Pass', body: 'A single non-repeatable command, confirmed received by the mixing unit' },
  fail: { title: 'Fail', body: 'Log, alert, hold, human review. The abort path is first-class.' },
  note: 'The limits these checks enforce are settled and approved by people in advance. Nothing consults a knowledge source while a command is being decided.',
};

/* ---------- Layer 5 · Actuation ---------- */
export const actuationLayer = {
  intro: 'An independent field controller with closed-loop flow verification and fail-safe firmware.',
  /* The unit's division of labour and its actuator complement are published:
     both are things an engineer building a fertigation rig would arrive at,
     and showing them is what makes the closed loop concrete. The parts that
     implement them are not named. */
  flow: [
    { n: 1, title: 'Receive', body: 'The field controller takes the confirmed command from the gateway' },
    { n: 2, title: 'Forward', body: 'Passed to the control processor over UART' },
    { n: 3, title: 'Validate', body: 'Integrity verified, then command and parameters decoded' },
    { n: 4, title: 'Actuate', body: 'Drives the dosing pumps, water pump, stirrer and outlet pump' },
    { n: 5, title: 'Monitor', body: 'Continuously watches the flow meter and the water-level sensor' },
    { n: 6, title: 'Report', body: 'Builds a status packet back through the gateway to the control panel' },
  ],
  guarantees: [
    { title: 'Idempotent execution', body: 'A retransmitted command never causes a second dose; delivery is assured through a single non-repeatable identifier.' },
    { title: 'Closed-loop flow verification', body: 'The controller integrates flow against the commanded volume. Zero flow, meaning dry or clogged, or excess flow, meaning a leak or stuck valve, aborts immediately.' },
    { title: 'Status uplink to the audit trail', body: 'Acknowledgement, actual dispensed volume and error codes are written to the record, so the server knows what physically happened.' },
    { title: 'Fail-safe firmware', body: 'Valves force-close on link loss, watchdog timeout or fault. Independent watchdogs enforce this outside application logic.' },
  ],
};

/* ---------- Layer 6 · Explanation ---------- */
export const explanationLayer = [
  {
    n: 1,
    title: 'Grounded retrieval',
    body: 'A searchable library of soil-science literature covering fertilizers, crop diseases and research. Retrieval ties every answer to a source and cuts invention.',
    note: 'Kept strictly to explanation. This library has no role once a decision is being made.',
  },
  {
    n: 2,
    title: 'An assistant that runs on site',
    body: 'A compact instruction-tuned language model served on the same box, on dedicated acceleration that keeps the processor free for the control loop.',
    note: 'Questions and farm data never leave the property.',
  },
  {
    n: 3,
    title: 'Control work comes first',
    body: 'Shared compute serves field and decision work ahead of user chat. Safety reasoning never waits behind a casual query.',
    note: 'Enforced ahead of the model, not inside it.',
  },
];

/* ---------- Guarantee register ----------
 * This replaced a decision matrix that paired each layer's requirement with
 * the property answering it. Individually those rows were unremarkable; eleven
 * of them together amounted to a complete design rationale, and the "why"
 * transfers more than the "what" does.
 *
 * So the second column stays (a promise is what a buyer is actually buying)
 * and the third answers "how would I know it was broken?" rather than "how did
 * you build it?". Keep that distinction when adding rows.
 */
export const guaranteeRegister = [
  {
    layer: 'Field sensing',
    guarantee: 'The system never acts on a reading it cannot date',
    evidence: 'Every measurement carries the time it was taken; a zone that goes quiet is raised as a fault, never assumed unchanged',
  },
  {
    layer: 'Carriage',
    guarantee: 'A corrupted reading can never become a dose',
    evidence: 'Anything failing its integrity check is discarded rather than stored, so it is not available to be acted on later',
  },
  {
    layer: 'On-site operation',
    guarantee: 'Losing the outside link never stops the farm being fed correctly',
    evidence: 'Every safety-critical service runs on the property, and is already running before a reading arrives',
  },
  {
    layer: 'The record',
    guarantee: 'What was intended and what happened are both recoverable',
    evidence: 'Both are written and kept side by side; any dose can be traced back to the reading that caused it',
  },
  {
    layer: 'Decision',
    guarantee: 'The same conditions always produce the same answer',
    evidence: 'Re-running a past decision against its original inputs reproduces it exactly, which makes disagreement testable',
  },
  {
    layer: 'Authorisation',
    guarantee: 'Nothing reaches hardware without clearing checks it cannot influence',
    evidence: 'The checks are evaluated independently of whatever proposed the dose, and one failure ends it outright',
  },
  {
    layer: 'Delivery',
    guarantee: 'What was commanded and what physically moved are compared',
    evidence: 'Delivery is measured as it happens; a mismatch in either direction aborts the dose and is recorded',
  },
  {
    layer: 'Explanation',
    guarantee: 'Nothing that can explain the system can change it',
    evidence: 'The assistant holds read access to the record and no route to an actuator: a boundary in the architecture, not a policy',
  },
];

/* ---------- The rule, restated ---------- */
export const closingRules = [
  'A deterministic decision path plus its gates are the only route to a valve',
  'The central safety gate is mandatory; the abort path is first-class',
  'Offline-first: connectivity is an additive overlay that may fail',
  'Operating limits are settled and approved in advance, never retrieved mid-decision',
  'Flow verified, commands confirmed and non-repeatable, every action audited',
  'Fail-safe by default; control work always outranks user chat',
];
